import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, ConfigEntityRow, ConfigSnapshot, GameInfoAuth } from '../api';
import type { Session } from '../App';
import { Container, PageHeader } from '../App';

interface Props {
  session: Session;
  games: GameInfoAuth[];
  activeGameId: string | null;
  onGameChange: (gameId: string) => void;
}

interface StatProps {
  label: string;
  value: number | string;
  hint?: string;
  accent?: 'brand' | 'good' | 'warn' | 'bad';
}

function Stat({ label, value, hint, accent }: StatProps) {
  const accentColor = {
    brand: 'text-brand',
    good: 'text-good',
    warn: 'text-warn',
    bad: 'text-bad',
  }[accent ?? 'brand'];
  return (
    <div className="card hover:border-line-strong transition-colors">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[11px] font-semibold tracking-[0.08em] uppercase text-fg-dim">
          {label}
        </div>
        <div className={`w-1.5 h-1.5 rounded-full ${accentColor.replace('text-', 'bg-')}`} />
      </div>
      <div className="text-[26px] font-semibold tracking-tight tabular-nums text-fg">
        {value}
      </div>
      {hint && <div className="text-xs text-fg-dim mt-1">{hint}</div>}
    </div>
  );
}

function WorkflowStep({
  index,
  title,
  detail,
  action,
  onClick,
  done,
}: {
  index: number;
  title: string;
  detail: string;
  action: string;
  onClick: () => void;
  done?: boolean;
}) {
  return (
    <div className="workflow-step">
      <div className={done ? 'workflow-index workflow-index-done' : 'workflow-index'}>
        {done ? '✓' : index}
      </div>
      <div className="min-w-0">
        <div className="font-semibold text-sm text-fg">{title}</div>
        <div className="text-xs text-fg-dim mt-0.5">{detail}</div>
      </div>
      <button className={done ? 'btn btn-ghost ml-auto' : 'btn ml-auto'} onClick={onClick}>
        {action}
      </button>
    </div>
  );
}

export default function Dashboard({ session, games, activeGameId, onGameChange }: Props) {
  const [entities, setEntities] = useState<ConfigEntityRow[]>([]);
  const [drafts, setDrafts] = useState<ConfigEntityRow[]>([]);
  const [snapshots, setSnapshots] = useState<ConfigSnapshot[]>([]);
  const [health, setHealth] = useState<string>('checking...');
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.health().then(h => setHealth(`${h.service} ${h.version}`)).catch(e => setHealth(`error: ${e.message}`));
  }, []);

  useEffect(() => {
    Promise.all([
      api.listEntities(session.gameId),
      api.listDrafts(session.gameId),
      api.listSnapshots(session.gameId),
    ])
      .then(([e, d, s]) => { setEntities(e); setDrafts(d); setSnapshots(s); })
      .catch(e => setError(e.message));
  }, [session.gameId]);

  const packages = new Set(entities.map(e => e.package));
  const current = snapshots.find(s => s.is_current);
  const activeGame = games.find(g => g.game_id === session.gameId);

  return (
    <Container>
      <PageHeader
        title={activeGame?.name || 'Dashboard'}
        subtitle="Build config, publish snapshots, then test package behavior with runtime tools."
        actions={
          <button className="btn btn-primary" onClick={() => navigate('/onboarding')}>
            Create new game
          </button>
        }
      />

      {error && <div className="error-banner">{error}</div>}

      <section className="mb-7">
        <div className="section-kicker">Start here</div>
        <div className="workflow-list">
          <WorkflowStep
            index={1}
            title="Create config entities"
            detail="Add resources, items, loot tables, quests, shops, and other package data."
            action={entities.length > 0 ? 'Open explorer' : 'Create first entity'}
            onClick={() => navigate('/explorer')}
            done={entities.length > 0}
          />
          <WorkflowStep
            index={2}
            title="Review and publish drafts"
            detail="Drafts stay private until they pass validation and are published as a snapshot."
            action={drafts.length > 0 ? 'Review drafts' : 'View publish flow'}
            onClick={() => navigate('/drafts')}
            done={drafts.length === 0 && snapshots.length > 0}
          />
          <WorkflowStep
            index={3}
            title="Exercise the game systems"
            detail="Use package tools to grant inventory, run battles, preview loot, and simulate economy changes."
            action="Open simulator"
            onClick={() => navigate('/m/simulator')}
            done={packages.size > 0}
          />
        </div>
      </section>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3.5 mb-7">
        <Stat label="Config entities" value={entities.length} accent="brand" />
        <Stat
          label="Pending drafts"
          value={drafts.length}
          accent={drafts.length > 0 ? 'warn' : 'brand'}
          hint={drafts.length === 0 ? 'No unpublished changes' : 'Awaiting publish'}
        />
        <Stat label="Snapshots" value={snapshots.length} accent="good" />
        <Stat label="Packages in use" value={packages.size} accent="brand" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-7">
        <div className="card">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold m-0">Server</h3>
            <span className="badge badge-published">live</span>
          </div>
          <div className="text-sm">
            <span className="text-fg-dim">Version</span>{' '}
            <strong className="font-mono">{health}</strong>
          </div>
          <div className="text-xs text-fg-muted mt-1">
            API health is checked from this browser session.
          </div>
        </div>

        <div className="card">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold m-0">Current snapshot</h3>
            {current && <span className="badge badge-current">current</span>}
          </div>
          {current ? (
            <>
              <div className="text-sm font-medium text-fg">{current.name}</div>
              <div className="text-xs text-fg-dim mt-1">
                {current.notes || <span className="text-fg-muted italic">no notes</span>}
              </div>
              <code className="block mt-2 text-[11.5px] text-fg-muted break-all">
                {current.id}
              </code>
            </>
          ) : (
            <div className="text-sm text-fg-dim">
              No snapshots yet. Create config entities, then publish drafts.
            </div>
          )}
        </div>
      </div>

      {/* User's games */}
      {games.length > 0 && (
        <div className="card">
          <div className="flex items-center justify-between gap-3 mb-3">
            <div>
              <h3 className="text-sm font-semibold m-0">Your games</h3>
              <div className="text-xs text-fg-dim mt-0.5">
                Switch workspaces without leaving the page.
              </div>
            </div>
            <button className="btn btn-primary" onClick={() => navigate('/onboarding')}>
              Create new game
            </button>
          </div>
          <div className="table-wrap">
            <table className="w-full text-sm">
              <thead>
                <tr>
                  <th className="text-left pb-2 text-fg-dim font-semibold text-xs uppercase tracking-wider">Name</th>
                  <th className="text-left pb-2 text-fg-dim font-semibold text-xs uppercase tracking-wider">Packages</th>
                  <th className="text-left pb-2 text-fg-dim font-semibold text-xs uppercase tracking-wider">Game ID</th>
                  <th className="text-left pb-2 text-fg-dim font-semibold text-xs uppercase tracking-wider">Action</th>
                </tr>
              </thead>
              <tbody>
                {games.map(g => (
                  <tr key={g.game_id} className="border-t border-line">
                    <td className="py-2 font-medium">
                      <div className="flex items-center gap-2">
                        <span>{g.name || 'Unnamed'}</span>
                        {g.game_id === activeGameId && <span className="badge badge-current">active</span>}
                      </div>
                    </td>
                    <td className="py-2 text-fg-dim">{g.enabled_packages.length} packages</td>
                    <td className="py-2">
                      <code className="text-xs text-fg-muted">{g.game_id.slice(0, 8)}...</code>
                    </td>
                    <td className="py-2">
                      <button
                        className="btn btn-ghost text-xs"
                        disabled={g.game_id === activeGameId}
                        onClick={() => onGameChange(g.game_id)}
                      >
                        {g.game_id === activeGameId ? 'Current' : 'Switch'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </Container>
  );
}
